import {
  DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS,
  PLAN_PROGRESS_GRAPH_DTMART_FIELD,
  PLAN_PROGRESS_GRAPH_FALLBACK_FIELD_FOR_ALL_CATEGORY,
  type PlanProgressGraphFieldItem,
  type PlanProgressGraphParams,
} from './dataPlanProgressGraph';

/**
 * API 取得対象の項目を解決する。
 * field / fields がない初回表示は undefined を返し、API 既定項目を維持する。
 */
export const resolveDataPlanProgressGraphFields = (
  params: PlanProgressGraphParams
): string[] | undefined => {
  if (params.fields && params.fields.length > 0) {
    return params.fields.slice(0, DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS);
  }
  if (params.field) {
    return [params.field];
  }
  return undefined;
};

export const resolveDataPlanProgressGraphFieldsForSearch = (
  existingParams: PlanProgressGraphParams | undefined
): string[] | undefined =>
  existingParams
    ? resolveDataPlanProgressGraphFields(existingParams)
    : undefined;

export const resolveDataPlanProgressGraphFieldsFromFavorite = (
  params: PlanProgressGraphParams
): string[] | undefined => resolveDataPlanProgressGraphFields(params);

/**
 * 種別全体へ切替時、出玉率（dtmart）を粗利合計へ置き換える。
 */
export const replaceDtmartFieldsForAllCategory = (
  fields: readonly string[]
): string[] =>
  fields.map((field) =>
    field === PLAN_PROGRESS_GRAPH_DTMART_FIELD
      ? PLAN_PROGRESS_GRAPH_FALLBACK_FIELD_FOR_ALL_CATEGORY
      : field
  );

export const buildDataPlanProgressGraphFieldsAfterAddEmptySlot = (
  currentFields: readonly string[],
  maxFields: number = DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS
): string[] =>
  currentFields.length >= maxFields
    ? currentFields.slice(0, maxFields)
    : [...currentFields, ''];

export const buildDataPlanProgressGraphFieldsAfterRemove = (
  currentFields: readonly string[],
  index: number,
  maxFields: number = DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= currentFields.length || currentFields.length <= 1) {
    return currentFields.slice(0, maxFields);
  }
  return currentFields
    .filter((_, fieldIndex) => fieldIndex !== index)
    .slice(0, maxFields);
};

export const buildDataPlanProgressGraphFieldsAfterIndexChange = (
  currentFields: readonly string[],
  index: number,
  field: string,
  maxFields: number = DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS
): string[] => {
  if (index < 0 || index >= maxFields) {
    return currentFields.slice(0, maxFields);
  }
  const slotCount = Math.min(
    Math.max(currentFields.length, index + 1),
    maxFields
  );
  return Array.from({ length: slotCount }, (_, slotIndex) =>
    slotIndex === index ? field : currentFields.at(slotIndex) ?? ''
  );
};

export const normalizeDataPlanProgressGraphFieldsForFetch = (
  fields: readonly string[],
  maxFields: number = DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS
): string[] => fields.filter((field) => field !== '').slice(0, maxFields);

export const shouldRefetchDataPlanProgressGraphFields = (
  previousFields: readonly string[],
  nextFields: readonly string[]
): boolean =>
  normalizeDataPlanProgressGraphFieldsForFetch(previousFields).join(',') !==
  normalizeDataPlanProgressGraphFieldsForFetch(nextFields).join(',');

const areRemainingFieldsInOrder = (
  remainingFields: readonly string[],
  originalFields: readonly string[]
): boolean =>
  remainingFields.reduce<{ searchIndex: number; isMatch: boolean }>(
    (state, field) => {
      if (!state.isMatch) {
        return state;
      }
      const relativeIndex = originalFields
        .slice(state.searchIndex)
        .findIndex((candidate) => candidate === field);
      return relativeIndex === -1
        ? { searchIndex: state.searchIndex, isMatch: false }
        : {
            searchIndex: state.searchIndex + relativeIndex + 1,
            isMatch: true,
          };
    },
    { searchIndex: 0, isMatch: true }
  ).isMatch;

export const isDataPlanProgressGraphFieldsRemovalOnly = (
  previousFields: readonly string[],
  nextFields: readonly string[]
): boolean => {
  const normalizedPrevious =
    normalizeDataPlanProgressGraphFieldsForFetch(previousFields);
  const normalizedNext =
    normalizeDataPlanProgressGraphFieldsForFetch(nextFields);
  return (
    normalizedNext.length < normalizedPrevious.length &&
    areRemainingFieldsInOrder(normalizedNext, normalizedPrevious)
  );
};

export const resolveDataPlanProgressGraphItemForFieldSlot = (
  graphList: readonly PlanProgressGraphFieldItem[],
  fieldSlots: readonly string[],
  slotIndex: number
): PlanProgressGraphFieldItem | undefined => {
  const field = fieldSlots.at(slotIndex);
  if (!field) {
    return undefined;
  }
  const graphListIndex = fieldSlots
    .slice(0, slotIndex)
    .filter((slotField) => slotField !== '').length;
  const itemByIndex = graphList.at(graphListIndex);
  return itemByIndex?.field === field
    ? itemByIndex
    : graphList.find((item) => item.field === field);
};

export const buildDataPlanProgressGraphListForFields = (
  currentGraphList: readonly PlanProgressGraphFieldItem[],
  fields: readonly string[]
): PlanProgressGraphFieldItem[] => {
  const graphByField = new Map(
    currentGraphList.map((item) => [item.field, item])
  );
  return fields.flatMap((field) => {
    const item = graphByField.get(field);
    return item ? [item] : [];
  });
};

export const mergeDataPlanProgressGraphFieldSlots = (
  previousSlots: readonly string[],
  graphList: readonly Pick<PlanProgressGraphFieldItem, 'field'>[],
  maxFields: number = DATA_PLAN_PROGRESS_GRAPH_MAX_FIELDS
): string[] => {
  if (graphList.length === 0) {
    return [];
  }
  const fieldsFromGraph = graphList.map((item) => item.field);
  const normalizedPrevious = normalizeDataPlanProgressGraphFieldsForFetch(
    previousSlots,
    maxFields
  );
  if (normalizedPrevious.join(',') === fieldsFromGraph.join(',')) {
    return previousSlots.slice(0, maxFields);
  }
  if (
    normalizedPrevious.length > fieldsFromGraph.length &&
    fieldsFromGraph.every(
      (field, index) => normalizedPrevious.at(index) === field
    )
  ) {
    return previousSlots.slice(0, maxFields);
  }
  const trailingEmptySlots = previousSlots
    .slice(fieldsFromGraph.length)
    .filter((field) => field === '');
  return [...fieldsFromGraph, ...trailingEmptySlots].slice(0, maxFields);
};

export { syncGraphSlotKeys } from './graphSlotKeys';
